import { forwardRef, useMemo } from 'react';
import ReactSelect, { components } from 'react-select';
import { TextSpan } from 'app/typography';
import { displayBreakpoints, isMobile, useWindowSize } from 'app/spacing';
import { dropdownColors } from 'app/colors';
import {
  StyledLabel,
  StyledLabelWrapper,
  reactSelectStyles,
  StyledCustomOption,
} from './styles';
import Icon from '../Icon';
import { useTranslation } from 'react-i18next';
import { DROPDOWN_MENU_ID } from 'hooks/useScrollMenuIntoView';

const getValue = (value, options) => {
  // if the value is a string, find that in the options and return it
  if (typeof value === 'string') {
    return options.find((option) => option.value === value) || null;
  }

  // if the value is an object, key off of the id
  if (value && 'id' in value) {
    return options.find((option) => option.value.id === value.id);
  }

  return value;
};

export const SingleValue = ({ children, ...propsSingleValue }) => {
  const Text = propsSingleValue.selectProps.TextComponent || TextSpan;
  return (
    <components.SingleValue {...propsSingleValue}>
      <Text
        value={propsSingleValue.selectProps.value}
        size={propsSingleValue.selectProps.textSize}
        style={{
          textIndent: '2px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          margin: 0,
        }}
      >
        {children}
      </Text>
    </components.SingleValue>
  );
};

const Menu = (props) => (
  <components.Menu
    {...props}
    innerProps={{ ...props.innerProps, id: DROPDOWN_MENU_ID }}
  />
);

const Select = forwardRef(
  (
    {
      className,
      label,
      textSize,
      size,
      style,
      styles,
      disabled,
      error,
      options,
      value,
      fullWidth,
      dropUp = false,
      components: componentsProp,
      onKeyDown,
      dropdownIcon,
      placeholder,
      isDisabledOption = false,
      wrapperDataQa,
      ...props
    },
    forwardedRef
  ) => {
    const { width } = useWindowSize();
    const { t } = useTranslation();
    const NoOptionsMessage = ({ children, ...propsNoOption }) => {
      return (
        <components.NoOptionsMessage {...propsNoOption}>
          <TextSpan
            size={textSize}
            style={{ color: 'var(--color-font-placeholder)' }}
          >
            {t('No Options')}
          </TextSpan>
        </components.NoOptionsMessage>
      );
    };

    const DropdownIndicator = () => (
      <Icon
        icon={dropdownIcon}
        size="2x"
        style={{
          textAlign: 'left',
          padding: '0 8px !important',
        }}
        color="var(--color-background-dark)"
      />
    );

    const updateOptions = useMemo(
      () =>
        isDisabledOption
          ? options.map((el) => ({
              ...el,
              isDisabled: el.value === value.value,
            }))
          : options,
      [isDisabledOption, options, value]
    );

    if (label) {
      return (
        <div
          className={className}
          data-qa-value={value?.value ?? value}
          data-qa-label={value?.label}
          data-qa={wrapperDataQa}
        >
          <StyledLabelWrapper>
            <StyledLabel disabled={disabled}>{label}</StyledLabel>
            <ReactSelect
              ref={forwardedRef}
              styles={{ ...reactSelectStyles, ...styles }}
              components={{
                SingleValue,
                NoOptionsMessage,
                DropdownIndicator,
                Option: StyledCustomOption,
                Menu,
                ...componentsProp,
              }}
              placeholder={
                //  When used Placholder component in components array click on placeholder is not triggering opening menu
                <TextSpan size={textSize} color="var(--color-font-placeholder)">
                  {placeholder}
                </TextSpan>
              }
              options={updateOptions}
              // TODO for some reason react-select anticipates a value of the shape { value, label },
              // i.e. one of the values in options.
              value={getValue(value, options)}
              mobile={isMobile(width, displayBreakpoints.desktop)}
              fullWidth={fullWidth}
              onKeyDown={(ev) => {
                if (onKeyDown) {
                  onKeyDown(ev, { options });
                }
              }}
              textSize={textSize}
              {...props}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: dropdownColors.hover,
                },
                controlHeight: 36,
              })}
              isDisabled={disabled}
              error={error}
              dropUp={dropUp}
            />
          </StyledLabelWrapper>
        </div>
      );
    }

    return (
      <div
        className={className}
        data-qa-value={value?.value ?? value}
        data-qa-label={value?.label}
        data-qa={wrapperDataQa}
      >
        <ReactSelect
          ref={forwardedRef}
          styles={{ ...reactSelectStyles, ...styles }}
          components={{
            SingleValue,
            NoOptionsMessage,
            DropdownIndicator,
            Option: StyledCustomOption,
            Menu,
            ...componentsProp,
          }}
          placeholder={
            //  When used Placholder component in components array click on placeholder is not triggering opening menu
            <TextSpan size={textSize} color="var(--color-font-placeholder)">
              {placeholder}
            </TextSpan>
          }
          options={updateOptions}
          // TODO for some reason react-select anticipates a value of the shape { value, label },
          // i.e. one of the values in options.
          value={getValue(value, options)}
          mobile={isMobile(width, displayBreakpoints.desktop)}
          fullWidth={fullWidth}
          onKeyDown={(ev) => {
            if (onKeyDown) {
              onKeyDown(ev, { options });
            }
          }}
          textSize={textSize}
          {...props}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary25: dropdownColors.hover,
            },
            controlHeight: 36,
          })}
          isDisabled={disabled}
          error={error}
        />
      </div>
    );
  }
);

Select.defaultProps = {
  value: null,
  label: '',
  onChange: () => {},
  options: null,
  className: '',
  textSize: 'text',
  placeholder: '',
  styles: {},
  size: 'small',
  disabled: false,
  error: false,
  fullWidth: false,
  inlineMenu: false,
  components: null,
  onKeyDown: null,
  dropdownIcon: 'down',
};

export default Select;
